/**
 * 翻译反馈管理组件
 * 管理员查看和处理用户翻译反馈
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquareWarning, 
  Check, 
  X, 
  Eye, 
  Trash2, 
  Filter,
  RefreshCw,
  ExternalLink,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 状态配置
const STATUS_CONFIG = {
  pending: { label: '待处理', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
  reviewed: { label: '已查看', color: 'bg-blue-100 text-blue-800', icon: '👁' },
  fixed: { label: '已修正', color: 'bg-green-100 text-green-800', icon: '✓' },
  ignored: { label: '已忽略', color: 'bg-gray-100 text-gray-800', icon: '−' },
};

// 语言配置
const LANGUAGES = {
  ru: { name: 'Русский', flag: '🇷🇺' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  fr: { name: 'Français', flag: '🇫🇷' },
  es: { name: 'Español', flag: '🇪🇸' },
  pt: { name: 'Português', flag: '🇵🇹' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  uz: { name: "O'zbekcha", flag: '🇺🇿' },
};

// 内容类型配置
const CONTENT_TYPES = {
  poster: { name: '海报', path: '/admin' },
  exhibition: { name: '画展', path: '/admin' },
  about: { name: '关于我们', path: '/admin' },
  artwork: { name: '艺术品', path: '/artworks' },
  product: { name: '产品', path: '/products' },
};

// 反馈类型
const FEEDBACK_TYPES = {
  inaccurate: '翻译不准确',
  incomplete: '翻译不完整',
  offensive: '内容不当',
  other: '其他问题',
};

const AdminTranslationFeedback = ({ token }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  
  // 筛选条件
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, [filterStatus, filterLanguage, filterContentType]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterLanguage !== 'all') params.append('language', filterLanguage);
      if (filterContentType !== 'all') params.append('content_type', filterContentType);
      
      const response = await axios.get(
        `${API}/translation-feedback/admin/list?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API}/translation-feedback/admin/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateFeedbackStatus = async (feedbackId, status, note = null) => {
    try {
      const data = { status };
      if (note !== null) data.admin_note = note;
      
      await axios.put(
        `${API}/translation-feedback/admin/${feedbackId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('状态已更新');
      fetchFeedbacks();
      fetchStats();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error('更新失败');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!confirm('确定要删除这条反馈吗？')) return;
    
    try {
      await axios.delete(
        `${API}/translation-feedback/admin/${feedbackId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('已删除');
      fetchFeedbacks();
      fetchStats();
    } catch (error) {
      toast.error('删除失败');
    }
  };

  const openDetail = (feedback) => {
    setSelectedFeedback(feedback);
    setAdminNote(feedback.admin_note || '');
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
              <div className="text-sm text-yellow-600">待处理</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-700">{stats.reviewed}</div>
              <div className="text-sm text-blue-600">已查看</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-700">{stats.fixed}</div>
              <div className="text-sm text-green-600">已修正</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-700">{stats.ignored}</div>
              <div className="text-sm text-gray-600">已忽略</div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-700">{stats.total}</div>
              <div className="text-sm text-amber-600">总计</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 筛选器 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquareWarning className="w-5 h-5" />
              翻译反馈管理
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => { fetchFeedbacks(); fetchStats(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              刷新
            </Button>
          </div>
          <CardDescription>查看和处理用户对自动翻译的反馈</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">筛选：</span>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部状态</SelectItem>
                <SelectItem value="pending">待处理</SelectItem>
                <SelectItem value="reviewed">已查看</SelectItem>
                <SelectItem value="fixed">已修正</SelectItem>
                <SelectItem value="ignored">已忽略</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterLanguage} onValueChange={setFilterLanguage}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="语言" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部语言</SelectItem>
                {Object.entries(LANGUAGES).map(([code, { name, flag }]) => (
                  <SelectItem key={code} value={code}>
                    {flag} {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterContentType} onValueChange={setFilterContentType}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部类型</SelectItem>
                {Object.entries(CONTENT_TYPES).map(([type, { name }]) => (
                  <SelectItem key={type} value={type}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 反馈列表 */}
          {loading ? (
            <div className="text-center py-8 text-gray-400">加载中...</div>
          ) : feedbacks.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <MessageSquareWarning className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>暂无反馈记录</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedbacks.map((feedback) => (
                <div
                  key={feedback.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* 头部信息 */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={STATUS_CONFIG[feedback.status]?.color}>
                          {STATUS_CONFIG[feedback.status]?.icon} {STATUS_CONFIG[feedback.status]?.label}
                        </Badge>
                        <span className="text-lg">
                          {LANGUAGES[feedback.language]?.flag}
                        </span>
                        <Badge variant="outline">
                          {CONTENT_TYPES[feedback.content_type]?.name || feedback.content_type}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {FEEDBACK_TYPES[feedback.feedback_type] || feedback.feedback_type}
                        </Badge>
                      </div>
                      
                      {/* 翻译内容 */}
                      <div className="text-sm space-y-1">
                        <p className="text-gray-500">
                          <span className="font-medium">原文：</span>
                          <span className="text-gray-700">{feedback.original_text?.slice(0, 100)}{feedback.original_text?.length > 100 ? '...' : ''}</span>
                        </p>
                        <p className="text-amber-600">
                          <span className="font-medium">翻译：</span>
                          <span>{feedback.translated_text?.slice(0, 100)}{feedback.translated_text?.length > 100 ? '...' : ''}</span>
                        </p>
                        {feedback.suggested_translation && (
                          <p className="text-green-600">
                            <span className="font-medium">建议：</span>
                            <span>{feedback.suggested_translation?.slice(0, 100)}</span>
                          </p>
                        )}
                      </div>
                      
                      {/* 提交信息 */}
                      <div className="text-xs text-gray-400 mt-2">
                        {feedback.submitted_by_name} · {formatDate(feedback.created_at)}
                      </div>
                    </div>
                    
                    {/* 操作按钮 */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(feedback)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            操作 <ChevronDown className="w-4 h-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => updateFeedbackStatus(feedback.id, 'reviewed')}>
                            <Eye className="w-4 h-4 mr-2" /> 标记已查看
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateFeedbackStatus(feedback.id, 'fixed')}>
                            <Check className="w-4 h-4 mr-2" /> 标记已修正
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateFeedbackStatus(feedback.id, 'ignored')}>
                            <X className="w-4 h-4 mr-2" /> 标记忽略
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => deleteFeedback(feedback.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> 删除
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 详情弹窗 */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareWarning className="w-5 h-5" />
              反馈详情
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4">
              {/* 状态和基本信息 */}
              <div className="flex flex-wrap gap-2">
                <Badge className={STATUS_CONFIG[selectedFeedback.status]?.color}>
                  {STATUS_CONFIG[selectedFeedback.status]?.label}
                </Badge>
                <span className="text-lg">{LANGUAGES[selectedFeedback.language]?.flag}</span>
                <span className="text-sm text-gray-500">
                  {LANGUAGES[selectedFeedback.language]?.name}
                </span>
                <Badge variant="outline">
                  {CONTENT_TYPES[selectedFeedback.content_type]?.name}
                </Badge>
                <Badge variant="secondary">
                  {FEEDBACK_TYPES[selectedFeedback.feedback_type]}
                </Badge>
              </div>

              {/* 内容对比 */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">原文（{selectedFeedback.field_name}）</div>
                  <p className="text-gray-800">{selectedFeedback.original_text}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <div className="text-xs text-amber-600 mb-1">当前翻译</div>
                  <p className="text-amber-800" dir={selectedFeedback.language === 'ar' ? 'rtl' : 'ltr'}>
                    {selectedFeedback.translated_text}
                  </p>
                </div>
                {selectedFeedback.suggested_translation && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs text-green-600 mb-1">用户建议翻译</div>
                    <p className="text-green-800" dir={selectedFeedback.language === 'ar' ? 'rtl' : 'ltr'}>
                      {selectedFeedback.suggested_translation}
                    </p>
                  </div>
                )}
                {selectedFeedback.user_comment && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-xs text-blue-600 mb-1">用户说明</div>
                    <p className="text-blue-800">{selectedFeedback.user_comment}</p>
                  </div>
                )}
              </div>

              {/* 提交信息 */}
              <div className="text-sm text-gray-500">
                提交者：{selectedFeedback.submitted_by_name} · {formatDate(selectedFeedback.created_at)}
              </div>

              {/* 管理员备注 */}
              <div className="space-y-2">
                <label className="text-sm font-medium">管理员备注</label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="添加处理备注..."
                  rows={2}
                />
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    // 跳转到对应内容编辑页面
                    const path = CONTENT_TYPES[selectedFeedback.content_type]?.path;
                    if (path) {
                      window.open(`${path}?highlight=${selectedFeedback.content_id}`, '_blank');
                    }
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  查看内容
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'ignored', adminNote)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    忽略
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'reviewed', adminNote)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    已查看
                  </Button>
                  <Button
                    className="bg-green-500 hover:bg-green-600"
                    onClick={() => updateFeedbackStatus(selectedFeedback.id, 'fixed', adminNote)}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    已修正
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTranslationFeedback;
